// Cloudflare R2 image upload from browser.
//
// Strategy
// --------
// AWS SDK v3 in the browser sometimes throws a `DataCloneError`
// ("Failed to execute 'postMessage' on 'Window': Request object could not be
// cloned") because something in the page (React dev overlay, dev-server HMR,
// browser extensions, ...) tries to postMessage an internal fetch Request and
// chokes on its non-cloneable body stream. To dodge this entirely we use AWS
// SDK ONLY to generate a pre-signed PUT URL, then perform the actual upload
// with a plain XMLHttpRequest — no fetch Request, no postMessage tug-of-war.
//
// Credentials are loaded from Firestore (settings/r2_config) and the document
// is only readable by admins (see firestore.rules).
//
// Firestore document shape: settings/r2_config = {
//   account_id: "...",
//   buckets: [{
//     id: "b1",
//     name: "vinyl-baku-1",
//     access_key: "...",
//     secret_key: "...",
//     public_url: "https://pub-xxx.r2.dev",
//     active: true
//   }, ...]
// }

import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";

const CONFIG_PATH = ["settings", "r2_config"];

export async function getR2Config() {
  const snap = await getDoc(doc(db, CONFIG_PATH[0], CONFIG_PATH[1]));
  return snap.exists() ? snap.data() : null;
}

export async function saveR2Config(data) {
  await setDoc(doc(db, CONFIG_PATH[0], CONFIG_PATH[1]), data, { merge: true });
}

function getActiveBucket(cfg) {
  if (!cfg?.buckets?.length) return null;
  return cfg.buckets.find((b) => b.active) || cfg.buckets[0];
}

function makeClient(account_id, bucket) {
  return new S3Client({
    region: "auto",
    endpoint: `https://${account_id}.r2.cloudflarestorage.com`,
    credentials: {
      accessKeyId: bucket.access_key,
      secretAccessKey: bucket.secret_key,
    },
    forcePathStyle: false,
  });
}

function sanitizeName(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9.\-_]+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80) || "file";
}

// Upload a Blob/File to a pre-signed URL using XMLHttpRequest. Resolves once
// the server returns 2xx. Rejects with a plain Error containing the readable
// status / text so React dev overlay never tries to clone any fetch Request.
function xhrPutFile(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // IMPORTANT: must send the SAME Content-Type that was used when signing
    // the URL — otherwise R2 returns 403 SignatureDoesNotMatch and the
    // browser surfaces it as a CORS / network error.
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded / ev.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        // eslint-disable-next-line no-console
        console.error("R2 PUT failed:", { status: xhr.status, body: xhr.responseText, url });
        reject(new Error(`HTTP ${xhr.status} — ${xhr.responseText || xhr.statusText || "upload failed"}`));
      }
    };
    xhr.onerror = () => {
      // eslint-disable-next-line no-console
      console.error("R2 PUT network/CORS error. URL:", url);
      reject(new Error(
        "Şəbəkə / CORS xətası. Cloudflare Dashboard → R2 → bucket → Settings → CORS Policy-də " +
        "şu konfiqurasiyanın olduğundan əmin olun: AllowedOrigins: [\"*\"] (və ya saytınızın origin-i), " +
        "AllowedMethods: [\"GET\",\"PUT\",\"POST\",\"DELETE\",\"HEAD\"], AllowedHeaders: [\"*\"]. " +
        "CORS yeniləmələri R2-də 30-60 saniyə təsir edə bilər. " +
        "DevTools Network tab-da `.r2.cloudflarestorage.com` çağırışına baxın — gerçək status orada görünür."
      ));
    };
    xhr.onabort = () => reject(new Error("Upload ləğv edildi."));
    xhr.send(file);
  });
}

export async function uploadImageToR2(file, { folder = "images", onProgress } = {}) {
  const cfg = await getR2Config();
  if (!cfg || !cfg.buckets?.length) {
    throw new Error("R2 ayarları əskıkdir. Admin Panel → Bucket-lər bölməsindən əlavə edin.");
  }
  const bucket = getActiveBucket(cfg);
  if (!bucket) throw new Error("Aktiv bucket tapılmadı.");
  if (!cfg.account_id) throw new Error("R2 account_id tapılmadı.");

  const ext = (file.name.match(/\.[a-zA-Z0-9]+$/) || [""])[0];
  const base = sanitizeName(file.name.replace(ext, ""));
  const key = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${base}${ext}`;
  // Resolve a Content-Type once and use the SAME value for both signing and the
  // XHR header. If the OS didn't know the file's mime (e.g. an .mov on some
  // systems) we infer a sensible default from the extension.
  const contentType = file.type || guessContentType(ext) || "application/octet-stream";
  const client = makeClient(cfg.account_id, bucket);

  // 1) Generate a presigned PUT URL with the SDK (no body, no network).
  let signedUrl;
  try {
    signedUrl = await getSignedUrl(
      client,
      new PutObjectCommand({
        Bucket: bucket.name,
        Key: key,
        ContentType: contentType,
      }),
      { expiresIn: 60 * 15 } // 15 minutes
    );
  } catch (err) {
    const code = err?.name || "PresignError";
    throw new Error(`Sign-URL xətası: ${code} — ${err?.message || String(err)}`);
  }

  // 2) Upload the actual bytes with XHR — completely bypasses fetch/postMessage.
  try {
    await xhrPutFile(signedUrl, file, contentType, onProgress);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("R2 upload failed:", err);
    throw new Error(`R2 upload xətası: ${err?.message || String(err)}`);
  }

  const publicBase = (bucket.public_url || "").replace(/\/$/, "");
  return `${publicBase}/${key}`;
}

function guessContentType(ext) {
  const e = (ext || "").toLowerCase().replace(".", "");
  const map = {
    mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", m4v: "video/mp4", mkv: "video/x-matroska",
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif",
    svg: "image/svg+xml", avif: "image/avif", heic: "image/heic",
    mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg", m4a: "audio/mp4", aac: "audio/aac", flac: "audio/flac",
  };
  return map[e] || "";
}

export function listBuckets(cfg) {
  return cfg?.buckets || [];
}
